import { Easing, Tween } from '@tweenjs/tween.js'
import { useMap } from '@vis.gl/react-google-maps'
import { useEffect, useRef } from 'react'
import {
  getGoogleMapCamera,
  getPaddedGoogleMapCameraTarget,
  getPanToCameraTarget,
  getZoomByCameraTarget,
  moveGoogleMapCamera,
  DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
  type GoogleCameraSnapshot,
  type GoogleCameraTarget,
} from './mapCamera'

type AnimateCameraOptions = {
  duration?: number
}

export function useGoogleMapCameraTween() {
  const map = useMap()
  const tweenRef = useRef<Tween<GoogleCameraSnapshot> | null>(null)
  const frameRef = useRef<number | null>(null)
  const loopRunningRef = useRef(false)

  useEffect(() => {
    return () => {
      tweenRef.current?.stop()
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
      }
      loopRunningRef.current = false
    }
  }, [])

  const ensureLoop = () => {
    if (loopRunningRef.current) return
    loopRunningRef.current = true

    const animate = (time: number) => {
      const tween = tweenRef.current

      if (tween && tween.update(time)) {
        frameRef.current = requestAnimationFrame(animate)
      } else {
        loopRunningRef.current = false
        frameRef.current = null
      }
    }

    frameRef.current = requestAnimationFrame(animate)
  }

  const animateCamera = (
    next: GoogleCameraTarget,
    options?: AnimateCameraOptions
  ) => {
    if (!map) return

    tweenRef.current?.stop()

    const camera = getGoogleMapCamera(map)
    if (!camera) return

    const duration =
      options?.duration ?? DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS
    const paddedNext = getPaddedGoogleMapCameraTarget(map, next)
    const targetCenter = paddedNext.center ?? {
      lat: camera.lat,
      lng: camera.lng,
    }
    const targetHeading = paddedNext.heading ?? camera.heading
    const targetTilt = paddedNext.tilt ?? camera.tilt
    const targetZoom = paddedNext.zoom ?? camera.zoom

    tweenRef.current = new Tween(camera)
      .to(
        {
          heading: targetHeading,
          lat: targetCenter.lat,
          lng: targetCenter.lng,
          tilt: targetTilt,
          zoom: targetZoom,
        },
        duration
      )
      .easing(Easing.Quadratic.Out)
      .onUpdate(() => {
        moveGoogleMapCamera(map, {
          center: {
            lat: camera.lat,
            lng: camera.lng,
          },
          heading: camera.heading,
          tilt: camera.tilt,
          zoom: camera.zoom,
        })
      })
      .onComplete(() => {
        tweenRef.current = null
      })
      .onStop(() => {
        tweenRef.current = null
      })
      .start()

    ensureLoop()
  }

  const zoomBy = (
    delta: number,
    duration = DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS
  ) => {
    if (!map) return

    animateCamera(getZoomByCameraTarget(map, delta), { duration })
  }

  const panTo = (
    center: google.maps.LatLngLiteral,
    duration = DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS
  ) => {
    animateCamera(getPanToCameraTarget(center), { duration })
  }

  return {
    animateCamera,
    panTo,
    zoomBy,
  }
}
